/**
 * The Inbox's three newer kinds - a draft item, a draft customer, a change
 * to a record that exists - as plain functions.
 *
 * ── WHY THESE ARE NOT IN THE PAGE ──
 * A change row is the one kind whose confirm alters a figure's FILING: a
 * ₹50,000 deposit that is nobody's becomes PKPP Tech's, for Consulting. The
 * sentence the row shows - "Customer: — → PKPP Tech" - is the whole basis on
 * which a person presses Confirm, so how it is built from the server's
 * `target.current` and `changes` is a function Node's test runner can pin,
 * as `inbox-books.ts` keeps the cross-book grouping out of the page. The
 * page only draws what these return.
 *
 * ── THE MIGRATION KIT (0018) ──
 * Two more shapes arrive here from an import: an invoice recreated from
 * another system's PDF (`imported`, with the number it had THERE), and a
 * SETTLEMENT - "this invoice was paid by that deposit, which is already in
 * the books". Both are decided by a person in the Inbox: the imported
 * invoice is recorded as issued without any sending (it was sent from the
 * old system), and the settlement records a payment and re-links the
 * existing deposit, moving the invoice's balance. The lines that say so -
 * "originally INV-2024-017 in Zoho", "Deposit … → payment against INV-0007",
 * "Balance ₹50,000 → ₹0" - are built below, for the same reason as the
 * before → after line: they are what Confirm is pressed on.
 */

// With the `.js` suffix, as the tests import this file: the compiled test
// runs under Node's ESM loader, which resolves nothing without one.
import { invoiceRef, isImportedDocument, originallyLine } from '../invoices/imported.js';

export { invoiceRef, originallyLine };

/**
 * The six kinds of row the Inbox can hold: three new records of money
 * (expense, deposit, invoice), two new records of the catalogue (a draft
 * item, a draft customer), and a CHANGE to a record that already exists.
 * Mirrors `PROPOSAL_TYPES` in `@fint/validation`.
 */
export type InboxKind = 'expense' | 'deposit' | 'invoice' | 'proforma' | 'item' | 'customer' | 'vendor' | 'change';

/**
 * The record a change proposal is about, as the server describes it: enough
 * to say "12 Aug · ₹50,000 · NEFT from PKPP" and what it is filed as NOW.
 * Every field but the id is optional on the wire, because the reader is
 * written against a contract the API is still landing - a row with a
 * missing field draws a dash, never a blank card.
 */
export interface ChangeTarget {
  type: ChangeTargetType;
  id: string;
  date?: string | null;
  amount?: string | null;
  currency?: string | null;
  description?: string | null;
  /** What the record says today - the "before" of before → after. */
  current?: ChangeNames | null;
  /**
   * A settlement's target is the INVOICE: its number here, the number it had
   * in the system it came from, and the balance the settlement would move.
   */
  number?: string | null;
  externalNumber?: string | null;
  total?: string | null;
  balanceDue?: string | null;
}

/**
 * What a change can be about: a deposit's filing, an expense's filing, or -
 * since 0018 - an invoice settled by a deposit that already arrived.
 * Mirrors `CHANGE_TARGET_TYPES` in `@fint/validation`.
 */
export type ChangeTargetType =
  | 'bank_transaction'
  | 'expense'
  | 'settlement'
  | 'refund'
  | 'payment'
  | 'bank_account'
  | 'book'
  | 'customer_details'
  | 'vendor_details'
  | 'bank_line';

/** The names behind the ids a change proposes or a record holds. */
export interface ChangeNames {
  partyName?: string | null;
  itemName?: string | null;
  categoryName?: string | null;
  /** An expense's counterparty. */
  vendorName?: string | null;
  /** The bill number as printed - what a matched receipt adds to a statement row. */
  referenceNumber?: string | null;
  /** The GST rate's name ("GST 18%"), when the bill shows one. */
  taxName?: string | null;
  /** On `current` only: the expense already has its bill attached as its source. */
  hasSourceFile?: boolean | null;
}

/** What a change proposes - the ids for the sheet, the names for the row. */
export interface ChangeSet extends ChangeNames {
  partyId?: string | null;
  itemId?: string | null;
  incomeCategoryId?: string | null;
  vendorId?: string | null;
  categoryId?: string | null;
  taxId?: string | null;
  /**
   * A settlement's changes: WHICH deposit pays the invoice, as the person
   * would recognise it on Banking, and how much of it. The deposit's own
   * filing today is optional on the wire; absent, it reads as uncategorised.
   */
  depositId?: string | null;
  depositDate?: string | null;
  depositAmount?: string | null;
  depositDescription?: string | null;
  /** How the deposit is filed today: a name, or null for none. Absent when the server does not say. */
  depositCategoryName?: string | null;
  amount?: string | null;
  /** What the customer withheld before paying - settles the invoice beside the cash. */
  tds?: { section?: string | null; rate?: number | null; amount?: string | null; deductorTan?: string | null } | null;
  bankCharges?: string | null;
}

/** The draft item's own fields, when the row carries them. */
export interface DraftItemFields {
  name?: string | null;
  type?: 'goods' | 'service' | null;
  rate?: string | null;
  unit?: string | null;
  description?: string | null;
  hsnSac?: string | null;
  sku?: string | null;
}

/** The draft customer's own fields, when the row carries them. */
export interface DraftCustomerFields {
  displayName?: string | null;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  gstin?: string | null;
}

/** What each kind is called on its badge and its sheet. */
export const KIND_LABEL: Record<InboxKind, string> = {
  expense: 'Expense',
  deposit: 'Deposit',
  invoice: 'Invoice draft',
  proforma: 'Quote draft',
  item: 'Item',
  customer: 'Customer',
  vendor: 'Vendor',
  change: 'Change',
};

/**
 * The mark beside a catalogue or change row, where a money row shows its
 * category's emoji. A draft item has no category; the box says what it is.
 */
export const KIND_ICON: Partial<Record<InboxKind, string>> = {
  item: '📦',
  customer: '🧑',
  change: '🔗',
};

/** Whether rows of this kind carry an amount of their own. A customer does not; a change shows its target's. */
export function hasOwnAmount(kind: InboxKind): boolean {
  return kind === 'expense' || kind === 'deposit' || kind === 'invoice' || kind === 'proforma';
}

/** An invoice row's own fields, when the server sends them beside the summary (0018). */
export interface DraftInvoiceFields {
  number?: string | null;
  externalNumber?: string | null;
  sourceSystem?: string | null;
  imported?: boolean | number | null;
  customerId?: string | null;
  customerName?: string | null;
  /** 'draft' while the customer waits in the same Inbox; 'active' once confirmed. */
  customerStatus?: string | null;
  dueDate?: string | null;
  sourceFile?: { id: string; filename: string } | null;
}

/**
 * What a row can carry about an import, in either of the two shapes the
 * wire has had: nested under `invoice`, as `item` and `customer` are, or
 * flat on the row. `importedFieldsOf` reads both so the page never has to
 * know which release of the API it is talking to.
 */
export interface ImportedFields extends DraftInvoiceFields {
  type: InboxKind;
  invoice?: DraftInvoiceFields | null;
  target?: ChangeTarget | null;
  /** On a change: what it proposes. */
  changes?: ChangeSet | null;
}

/** The import fields of a row, flat, whichever shape they arrived in. The nested object wins where both are present. */
export function importedFieldsOf(item: ImportedFields): DraftInvoiceFields {
  const nested = item.invoice ?? {};
  return {
    number: nested.number ?? item.number ?? null,
    externalNumber: nested.externalNumber ?? item.externalNumber ?? null,
    sourceSystem: nested.sourceSystem ?? item.sourceSystem ?? null,
    imported: nested.imported ?? item.imported ?? null,
    customerId: nested.customerId ?? item.customerId ?? null,
    customerName: nested.customerName ?? item.customerName ?? null,
    customerStatus: nested.customerStatus ?? item.customerStatus ?? null,
    dueDate: nested.dueDate ?? item.dueDate ?? null,
    sourceFile: nested.sourceFile ?? item.sourceFile ?? null,
  };
}

/** Whether the row is an invoice recreated from another system - see `isImportedDocument` for what counts. */
export function isImportedInvoice(item: ImportedFields): boolean {
  return item.type === 'invoice' && isImportedDocument(importedFieldsOf(item));
}

/** Whether the row is a change that settles an invoice with a deposit. */
export function isSettlement(item: ImportedFields): boolean {
  return item.type === 'change' && item.target?.type === 'settlement';
}

/**
 * What the row's action column offers.
 *
 *   edit           the Inbox's own sheet (money rows, the catalogue, a filing change)
 *   open-invoice   a link to the invoice page, where a home-grown draft is SENT
 *   record-issued  a Confirm of this one row, worded for what it does: an
 *                  imported invoice was sent from the old system already, so
 *                  confirming records it as issued and sends nothing
 *
 * A settlement has nothing to edit in a sheet - its two halves are records
 * that exist - so it links to the invoice it would settle.
 */
export type RowAction = 'edit' | 'open-invoice' | 'open-proforma' | 'record-issued' | 'correction' | 'discard-only';

export function rowActionFor(item: ImportedFields): RowAction {
  if (isImportedInvoice(item)) return 'record-issued';
  if (item.type === 'invoice' || isSettlement(item)) return 'open-invoice';
  // A quote is edited and sent on its own page, as a draft invoice is.
  if (item.type === 'proforma') return 'open-proforma';
  // A proposed vendor is a name and a GSTIN: confirmed by its tick, discarded
  // from its row, and edited on the Vendors page once it is real.
  if (item.type === 'vendor') return 'discard-only';
  if (isCorrection(item)) return 'correction';
  return 'edit';
}

/**
 * What a change DOES, in two words, for the type column - "Refund", "Date
 * fix", "Delete" - instead of one word, "Change", on every row. Sai read a
 * column of ten identical "Change" badges and could not tell a refund from a
 * deletion without reading each sentence; the badge is what the eye scans.
 *
 * An expense change that names several things is labelled by the one that
 * matters most to a figure: a deletion, then a category (which decides where
 * the money is counted), then a date, then a label, then the bill details.
 */
export function changeKindLabel(item: ImportedFields): string {
  const type = item.target?.type;
  const changes = (item.changes ?? {}) as Record<string, unknown>;
  switch (type) {
    case 'settlement':
      return 'Settle invoice';
    case 'refund':
      return 'Refund';
    case 'payment':
      return 'Payment fix';
    case 'bank_account':
      return 'Account details';
    case 'bank_transaction':
      return 'Who paid';
    case 'book':
      return 'Book details';
    case 'customer_details':
      return 'Customer details';
    case 'vendor_details':
      return 'Vendor details';
    case 'bank_line':
      return changes.fileAs === 'drawing' ? 'Drawing' : changes.fileAs === 'capital' ? 'Capital' : 'Income';
    case 'expense':
      if ('delete' in changes) return 'Delete';
      if ('categoryId' in changes) return 'Category fix';
      if ('date' in changes) return 'Date fix';
      if ('notes' in changes) return 'Label fix';
      return 'Bill details';
    default:
      return 'Change';
  }
}

/**
 * A correction to a recorded row (R-06): a refund, a payment's account or
 * amount, an account's details, or an expense's date, label or deletion.
 * The filing sheet edits vendors and categories; opened on one of these it
 * would offer the wrong fields and could re-propose the change without the
 * part that mattered. So it has no Edit - the row's sentence says exactly
 * what Confirm does, and a wrong one is discarded and proposed again.
 */
export function isCorrection(item: ImportedFields): boolean {
  if (item.type !== 'change') return false;
  const type = item.target?.type;
  if (
    type === 'refund' ||
    type === 'payment' ||
    type === 'bank_account' ||
    type === 'book' ||
    type === 'customer_details' ||
    type === 'vendor_details' ||
    type === 'bank_line'
  )
    return true;
  const changes = (item.changes ?? {}) as Record<string, unknown>;
  return type === 'expense' && ('delete' in changes || 'date' in changes || 'notes' in changes);
}

/** The `@fint/money` functions a settlement's figures need, handed in - see overview-months.ts for why not imported. */
export type SettlementMoneyKit = Pick<typeof import('@fint/money'), 'parse' | 'add' | 'subtract' | 'format' | 'zero' | 'max' | 'min'>;

export interface SettlementFigures {
  /** What the settlement records: the smaller of the deposit and the balance, unless the change names an amount. */
  amount: string;
  /** The invoice's balance today, and after the payment is recorded. */
  balanceBefore: string;
  balanceAfter: string;
  /**
   * How the deposit is filed today, as the before half of the line: the
   * category's name, or "uncategorised income" when the server says it has
   * none. Null when the server does not say - the line then omits the
   * clause rather than guess, because "uncategorised" over a deposit filed
   * as Services would be a false statement on the row Confirm is pressed on.
   */
  depositFiling: string | null;
  /** "INV-0007 (originally INV-2024-017)". */
  invoice: string;
  /** What was withheld, as clauses for the line: "₹200 TDS (194C)", "₹25 bank charges". Empty when nothing was. */
  withheld: Array<{ label: string; amount: string }>;
}

/**
 * The figures a settlement row shows: what pressing Confirm would record.
 *
 * The amount follows the server's rule ("the smaller of the deposit and the
 * invoice balance") when the change names none, so the row and the payment
 * cannot disagree. The balance after is never below zero - a deposit larger
 * than the balance leaves the rest unapplied on the payment, not a negative
 * invoice. Null when the target is not a settlement, or carries no balance
 * to move: a half-shaped row draws nothing rather than "₹0 → ₹0".
 */
export function settlementFigures(
  target: ChangeTarget | null | undefined,
  changes: ChangeSet | null | undefined,
  currency: string,
  kit: SettlementMoneyKit,
): SettlementFigures | null {
  if (target?.type !== 'settlement' || !changes) return null;
  if (target.balanceDue == null && changes.amount == null) return null;
  const nothing = kit.zero(currency);
  const balance = target.balanceDue != null ? kit.parse(target.balanceDue, currency) : nothing;
  const deposit = changes.depositAmount != null ? kit.parse(changes.depositAmount, currency) : null;
  // The deposit plus what was withheld is what can settle - the server's
  // rule; a TDS-short deposit still pays the invoice in full.
  const withheld: SettlementFigures['withheld'] = [];
  let withheldTotal = nothing;
  if (changes.tds?.amount) {
    withheld.push({ label: `TDS${changes.tds.section ? ` (${changes.tds.section})` : ''}`, amount: changes.tds.amount });
    withheldTotal = kit.add(withheldTotal, kit.parse(changes.tds.amount, currency));
  }
  if (changes.bankCharges) {
    withheld.push({ label: 'bank charges', amount: changes.bankCharges });
    withheldTotal = kit.add(withheldTotal, kit.parse(changes.bankCharges, currency));
  }
  const settleable = deposit ? kit.add(deposit, withheldTotal) : null;
  const named = changes.amount != null ? kit.parse(changes.amount, currency) : null;
  const amount = named ?? (settleable ? kit.min(settleable, balance) : balance);
  return {
    withheld,
    amount: kit.format(amount),
    balanceBefore: kit.format(balance),
    balanceAfter: kit.format(kit.max(kit.subtract(balance, amount), nothing)),
    depositFiling:
      'depositCategoryName' in changes ? changes.depositCategoryName?.trim() || 'uncategorised income' : null,
    invoice: invoiceRef(target.number, target.externalNumber),
  };
}

const DASH = '—';

/** One "Label: before → after" clause, or null when the change does not touch this field. */
function clause(
  label: string,
  proposed: string | null | undefined,
  current: string | null | undefined,
  proposes: boolean,
): string | null {
  if (!proposes) return null;
  return `${label}: ${current?.trim() || DASH} → ${proposed?.trim() || DASH}`;
}

/**
 * "Customer: — → PKPP Tech · Item: — → Consulting" - one clause per field
 * the change proposes, before → after, in the order a person reads a
 * deposit: who, for what, filed as. A field the change does not name is
 * left out rather than shown unchanged, so the line is only what pressing
 * Confirm would do. A field proposed as CLEARED reads "PKPP Tech → —".
 *
 * Which fields count as proposed is the presence of the id key (or its
 * name, when the server sends names only): `partyId: null` is a proposal to
 * clear, an absent key is no proposal.
 */
export function beforeAfterLine(target: ChangeTarget | null | undefined, changes: ChangeSet | null | undefined): string {
  if (!changes) return '';
  // A settlement's before → after is figures, not names - see `settlementFigures`.
  if (target?.type === 'settlement') return '';
  const current: ChangeNames = target?.current ?? {};
  const proposes = (idKey: keyof ChangeSet, nameKey: keyof ChangeNames) => idKey in changes || nameKey in changes;
  const clauses =
    target?.type === 'expense'
      ? [
          clause('Vendor', changes.vendorName, current.vendorName, proposes('vendorId', 'vendorName')),
          clause('Category', changes.categoryName, current.categoryName, proposes('categoryId', 'categoryName')),
          // What a matched bill adds to a statement row (0018): its number
          // and its GST rate. The reference is proposed as the value itself,
          // so its own key is the sign; the rate by id or by name.
          clause('Reference', changes.referenceNumber, current.referenceNumber, 'referenceNumber' in changes),
          clause('GST', changes.taxName, current.taxName, proposes('taxId', 'taxName')),
        ]
      : [
          clause('Customer', changes.partyName, current.partyName, proposes('partyId', 'partyName')),
          clause('Item', changes.itemName, current.itemName, proposes('itemId', 'itemName')),
          clause('Category', changes.categoryName, current.categoryName, proposes('incomeCategoryId', 'categoryName')),
        ];
  return clauses.filter((c): c is string => c !== null).join(' · ');
}

export interface ChangeForm {
  partyId: string;
  itemId: string;
  categoryId: string;
  vendorId: string;
}

/**
 * What a change's Edit sheet starts with: the PROPOSED ids, so the selects
 * open on what Confirm would produce, and a person who changes nothing and
 * saves has re-proposed exactly the same thing.
 */
export function proposedIds(changes: ChangeSet | null | undefined): ChangeForm {
  return {
    partyId: changes?.partyId ?? '',
    itemId: changes?.itemId ?? '',
    categoryId: changes?.incomeCategoryId ?? changes?.categoryId ?? '',
    vendorId: changes?.vendorId ?? '',
  };
}

/**
 * The body the sheet sends back: only the fields that differ from what the
 * change already proposed, keyed the way the target's change schema keys
 * them - `partyId`/`itemId`/`incomeCategoryId` for a deposit,
 * `vendorId`/`categoryId` for an expense. An emptied select on a field the
 * change had proposed sends `null` (clear it); an empty select on a field it
 * never proposed sends nothing (still not proposed).
 */
export function changeBody(
  targetType: ChangeTarget['type'] | undefined,
  before: ChangeSet | null | undefined,
  form: ChangeForm,
): Record<string, string | null> {
  const body: Record<string, string | null> = {};
  const put = (key: keyof ChangeSet, value: string) => {
    const had = before ? key in before : false;
    const was = before?.[key] ?? '';
    if (value === was && (had || value === '')) return;
    if (value === '' && !had) return;
    body[key] = value || null;
  };
  // A settlement is not edited in the sheet - see `rowActionFor`.
  if (targetType === 'settlement') return body;
  if (targetType === 'expense') {
    put('vendorId', form.vendorId);
    put('categoryId', form.categoryId);
  } else {
    put('partyId', form.partyId);
    put('itemId', form.itemId);
    put('incomeCategoryId', form.categoryId);
  }
  return body;
}
